/*
 * Headquiet theme script (module, loaded once from snippets/hq-head.liquid).
 * Custom elements, so sections work again after the theme editor re-renders them:
 *   hq-volume      the Volume Down hero: scroll turns the page down (--p from 0 to 1)
 *   hq-zones       tension map lights the zone of the hovered / focused row
 *   hq-shop        zone chips dim the cards of other zones
 *   hq-reel        a clip plays in place on tap, one at a time
 *   hq-join-steps  two-step sign-up on the customer form
 *   hq-copy        copy a code
 * Guards: reduced motion, html.factory-lowfx (weak devices, Save-Data, in-app browsers) and the motion setting
 * (html.hq-anim) get the still hero; hover effects only with a fine pointer; transform / opacity only.
 */

const root = document.documentElement;
const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const motionOn = () => root.classList.contains('hq-anim') && !reduceQuery.matches && !root.classList.contains('factory-lowfx');

/* Horizon 4.2 scrolls .page-wrapper from 990px up, the document below that. */
function scroller() {
  const wrap = document.querySelector('.page-wrapper');
  if (wrap && wrap.scrollHeight > wrap.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(wrap).overflowY)) return wrap;
  return null;
}
const scrollTopOf = (el) => (el ? el.scrollTop : window.scrollY || document.documentElement.scrollTop);

/* ------------------------------------------------------------------ Volume Down hero */
class HqVolume extends HTMLElement {
  connectedCallback() {
    this.p = -1;
    this.knob = this.querySelector('[data-hq-knob]');
    this.distance = Number(this.dataset.distance) || 400;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    this.onResize = () => {
      this.measure();
      this.update();
    };
    this.onKnob = () => this.turn();
    this.knob?.addEventListener('click', this.onKnob);

    if (!motionOn() || this.classList.contains('hq-vd--still')) {
      this.set(1);
      return;
    }
    this.measure();
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.update();
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(([entry]) => this.classList.toggle('is-off', !entry.isIntersecting));
      this.io.observe(this);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onResize);
    this.knob?.removeEventListener('click', this.onKnob);
    this.io?.disconnect();
  }

  /* where the turn starts: the hero's top in the scrolling box, minus what sits above it at the top of the page */
  measure() {
    const box = scroller();
    // Horizon renders #header-group as display: contents with a sticky header: add up its children, like Horizon does
    const group = document.getElementById('header-group');
    let groupH = 0;
    if (group) for (const el of group.children) if (el instanceof HTMLElement) groupH += el.offsetHeight;
    const top = this.getBoundingClientRect().top + scrollTopOf(box);
    this.start = Math.max(0, top - groupH);
  }

  update() {
    const y = scrollTopOf(scroller());
    this.set(Math.min(1, Math.max(0, (y - this.start) / this.distance)));
  }

  set(p) {
    if (p === this.p) return;
    this.p = p;
    this.style.setProperty('--p', p.toFixed(4));
    this.classList.toggle('is-quiet', p > 0.6);
    this.classList.toggle('is-flat', p >= 1);
    if (this.knob) this.knob.setAttribute('aria-label', p >= 1 ? this.dataset.labelUp || '' : this.dataset.labelDown || '');
  }

  turn() {
    if (!motionOn()) return;
    const box = scroller();
    const target = this.p >= 1 ? this.start : this.start + this.distance + 20;
    (box || window).scrollTo({ top: target, behavior: 'smooth' });
  }
}
if (!customElements.get('hq-volume')) customElements.define('hq-volume', HqVolume);

/* ------------------------------------------------------------------ zones */
class HqZones extends HTMLElement {
  connectedCallback() {
    this.light = (zone) => {
      this.querySelectorAll('.hq-z').forEach((u) => u.classList.toggle('is-on', u.classList.contains(`hq-z--${zone}`)));
      this.querySelectorAll('[data-lbl]').forEach((t) => t.classList.toggle('is-on', t.dataset.lbl === zone));
      this.querySelectorAll('[data-zone-hover]').forEach((r) => r.classList.toggle('is-on', r.dataset.zoneHover === zone));
    };
    this.querySelectorAll('[data-zone-hover]').forEach((row) => {
      row.addEventListener('mouseenter', () => this.light(row.dataset.zoneHover));
      row.addEventListener('focus', () => this.light(row.dataset.zoneHover));
    });
  }
}
if (!customElements.get('hq-zones')) customElements.define('hq-zones', HqZones);

/* ------------------------------------------------------------------ product grid chips */
class HqShop extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-chip]');
      if (!chip || !this.contains(chip)) return;
      const zone = chip.dataset.chip;
      if (zone) this.dataset.zone = zone;
      else delete this.dataset.zone;
      this.querySelectorAll('[data-chip]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    });
  }
}
if (!customElements.get('hq-shop')) customElements.define('hq-shop', HqShop);

/* ------------------------------------------------------------------ clips */
class HqReel extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('.hq-reel__play');
    if (!this.video || !this.button) return;
    this.button.addEventListener('click', () => (this.classList.contains('is-playing') ? this.stop() : this.play()));
    this.video.addEventListener('ended', () => this.stop());
  }

  play() {
    document.querySelectorAll('hq-reel.is-playing').forEach((r) => r !== this && r.stop && r.stop());
    this.video.muted = false;
    const started = this.video.play();
    this.classList.add('is-playing');
    this.label(true);
    if (started && started.catch) started.catch(() => this.stop());
  }

  stop() {
    this.video?.pause();
    this.classList.remove('is-playing');
    this.label(false);
  }

  label(playing) {
    const caption = this.querySelector('.hq-reel__cap strong')?.textContent || '';
    const word = playing ? this.button.dataset.labelPause : this.button.dataset.labelPlay;
    this.button.setAttribute('aria-label', `${word}: ${caption}`);
  }
}
if (!customElements.get('hq-reel')) customElements.define('hq-reel', HqReel);

/* ------------------------------------------------------------------ two-step sign-up */
class HqJoinSteps extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.mail = this.querySelector('input[type="email"]');
    this.tags = this.querySelector('[data-join-tags]');
    this.error = this.querySelector('[data-join-error]');
    this.picked = new Set();
    this.querySelector('[data-join-next]')?.addEventListener('click', () => this.next());
    this.mail?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.next();
      }
    });
    this.querySelectorAll('[data-join-tag]').forEach((chip) =>
      chip.addEventListener('click', () => {
        const on = chip.getAttribute('aria-pressed') !== 'true';
        chip.setAttribute('aria-pressed', String(on));
        if (on) this.picked.add(chip.dataset.joinTag);
        else this.picked.delete(chip.dataset.joinTag);
        this.writeTags();
      })
    );
    this.querySelector('[data-join-skip]')?.addEventListener('click', () => {
      this.picked.clear();
      this.writeTags();
    });
    this.form?.addEventListener('submit', (event) => {
      if (!this.step(2).classList.contains('is-on')) {
        event.preventDefault();
        this.next();
      }
    });
  }

  step(n) {
    return this.querySelector(`[data-step="${n}"]`);
  }

  valid() {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((this.mail?.value || '').trim());
  }

  next() {
    if (!this.valid()) {
      if (this.error) this.error.hidden = false;
      this.mail?.setAttribute('aria-invalid', 'true');
      this.mail?.focus();
      return;
    }
    if (this.error) this.error.hidden = true;
    this.mail?.removeAttribute('aria-invalid');
    this.step(1).classList.remove('is-on');
    this.step(2).classList.add('is-on');
    this.step(2).querySelector('button')?.focus({ preventScroll: true });
  }

  writeTags() {
    if (!this.tags) return;
    this.tags.value = [this.tags.dataset.base, ...this.picked].filter(Boolean).join(', ');
  }
}
if (!customElements.get('hq-join-steps')) customElements.define('hq-join-steps', HqJoinSteps);

/* ------------------------------------------------------------------ copy a code */
class HqCopy extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', () => {
      const text = this.dataset.copy || '';
      const was = button.textContent;
      const done = () => {
        button.textContent = this.dataset.done || was;
        setTimeout(() => (button.textContent = was), 1400);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(text).then(done, done);
      else done();
    });
  }
}
if (!customElements.get('hq-copy')) customElements.define('hq-copy', HqCopy);

/* ------------------------------------------------------------------ collection sort: submit on change */
document.addEventListener('change', (event) => {
  const select = event.target.closest && event.target.closest('[data-hq-sort] select');
  if (select) select.form.submit();
});

/* the join form reloads the page after posting: bring the code into view */
if (window.location.search.includes('customer_posted=true')) {
  requestAnimationFrame(() => document.querySelector('.hq-join__done')?.focus({ preventScroll: false }));
}
